import { BehaviorSubject, Observable, ReplaySubject, of } from 'rxjs';
import { environment, FEATURES, getValidationErrorMessage } from '@utility/app.util';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import * as Sentry from "@sentry/angular-ivy";

import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { NavigationExtras, Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';


import { AbstractAuthService } from './abstract-auth.service';
import { I18nService } from '@app/core/transloco/i18n.service';
import { NotificationService } from '@utility/services/notification.service';
import { clearSentryContext, setSentryContext } from '@app/utility/sentry-error-handlers.utils';
import { EchoService } from '@app/core/services/echo.service';

import {
  UserInfoDto,
  ISiteManager,
  ClientInfoDto,
  RefreshTokenDto,
  DefaultResponseDto,
  ClientInfoResponseDto,
} from '@sociuu/interfaces';
import { IBecomeUser, IBecomeUserServer } from '@lib/become-user.interface';
import { IMagicLogin, IMagicLoginServer, ILogin, ILoginServer, IAccountReset, IAccountUpdate, IAccountUpdatePayload, IEnable2faResponse } from '@lib/auth-token.interface';

import { INTEGRATIONS } from '@app/utility/app.util';

import { MatDialogRef } from '@angular/material/dialog';
import { FuseConfirmationConfig, FuseConfirmationService } from '@fuse/services/confirmation';
import { FuseConfirmationDialogComponent } from '@fuse/services/confirmation/dialog/dialog.component';

const RESTRICTED_ACTION_MESSAGE: string = 'Sorry, you are not allowed to perform this action!';
const DEFAULT_COMPANY_LOGO = 'assets/images/logo/logo-text-on-dark.svg';

export enum UserTypeIds {
  SUPER_ADMIN = 1,
  ADMIN = 2,
  REGULAR_USER = 3,
};

@Injectable({
  providedIn: 'root',
})
export class AuthService extends AbstractAuthService {
  public isSociuuhub: boolean = false;

  private _currentUser$: BehaviorSubject<UserInfoDto> = new BehaviorSubject<UserInfoDto>(null as unknown as UserInfoDto);

  private _clientInfo$ = new BehaviorSubject<ClientInfoDto>(null as unknown as ClientInfoDto);
  private _clientInfoRequest$: Observable<ClientInfoResponseDto> | null = null;
  private _isActimoIntegrationEnabled: ReplaySubject<boolean> = new ReplaySubject(1);
  private _isBrandwatchIntegrationEnabled: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public get clientInfo(): BehaviorSubject<ClientInfoDto> {
    return this._clientInfo$;
  }

  public get siteManager(): ISiteManager {
    return {
      site_manager_name: this.clientInfo.value.site_manager_name,
      site_manager_email: this.clientInfo.value.site_manager_email,
      sociuu_support_email: this.clientInfo.value.sociuu_support_email,
    }
  }

  public get isSociuuHubEnabled(): boolean {
    return !!this.clientInfo.value.is_sociuuhub;
  }

  public get isIndividualStatsEnabled(): boolean {
    return !!this.clientInfo.value.individual_stats;
  }

  public get isSMSEnabled(): boolean {
    return !!this.clientInfo?.value?.distributions?.some((distribution: string): boolean => distribution === 'sms');
  }

  public get isSMSCreditsAvailable(): boolean {
    return this.isSMSEnabled && this.clientInfo?.value?.available_credit >= 1;
  }

  // Feature getters start ============================== //
  private get features(): string[] {
    return this.clientInfo?.value?.features || [];
  }

  public get isSSOFeatureEnabled(): boolean {
    return this.features?.includes(FEATURES.SSO);
  }

  public get isCookieConsentEnabled(): boolean {
    return this.features?.includes(FEATURES.COOKIE_CONSENT);
  }

  public get isSmartFlowFeatureEnabled(): boolean {
    return this.features?.includes(FEATURES.AUTO_FLOW);
  }

  public get isSelfSignupFeatureEnabled(): boolean {
    return this.features?.includes(FEATURES.SELF_SIGNUP);
  }

  public get isSocialConnectFeatureEnabled(): boolean {
    return this.features?.includes(FEATURES.SOCIAL_CONNECT);
  }

  public get isEntraFeatureEnabled(): boolean {
    return this.features?.includes(FEATURES.ENTRA);
  }

  public get isUsergroupCountryAssociationFeatureEnabled(): boolean {
    return this.features?.includes(FEATURES.USER_GROUP_COUNTRY_ASSOCIATION);
  }

  public get isOptInFeatureEnabled(): boolean {
    return !!this.clientInfo.value.opt_in || this.features?.includes(FEATURES.OPT_IN);
  }

  // Feature getters end ============================== //

  // Integration getters start ============================== //

  public get isBrandwatchIntegrationEnabled(): BehaviorSubject<boolean> {
    return this._isBrandwatchIntegrationEnabled;
  }

  public get isActimoIntegrationEnabled(): ReplaySubject<boolean> {
    return this._isActimoIntegrationEnabled;
  }

  // Integration getters end ============================== //

  public get currentUser(): BehaviorSubject<UserInfoDto> {
    return this._currentUser$;
  }

  public set isSociuuHubEnabled(data: boolean) {
    this._clientInfo$.next({
      ...this._clientInfo$.value,
      is_sociuuhub: data ? 1 : 0,
    });
  }

  public get isBecomeUserEnabled(): boolean {
    return localStorage.getItem('isBecomeUser') === 'true';
  }

  public get isBecomeUserByAdmin(): boolean {
    return this.isBecomeUserEnabled && localStorage.getItem('becomeUserByTypeID') === UserTypeIds.ADMIN.toString();
  }

  public get isBecomeUserBySuperAdmin(): boolean {
    return this.isBecomeUserEnabled && localStorage.getItem('becomeUserByTypeID') === UserTypeIds.SUPER_ADMIN.toString();
  }

  public get isCookieConsentAccepted(): boolean {
    return this.cookieService.get('cookieConsentAccepted') === 'true';
  }

  public get checkIfCookieConsentAccepted(): boolean {
    return this.cookieService.get('cookieConsentAccepted') === 'true';
  }

  public get shouldShowCookieConsent(): boolean {
    return this.isCookieConsentEnabled && !this.isCookieConsentAccepted;
  }

  public get token(): string {
    return localStorage.getItem('token') || '';
  }

  public set token(token: string) {
    localStorage.setItem('token', token);
    this.echoService.resetEchoAuth(token);
  }

  constructor(
    private router: Router,
    private http: HttpClient,
    private echoService: EchoService,
    private i18nService: I18nService,
    private cookieService: CookieService,
    private notificationService: NotificationService,
    private _fuseConfirmationService: FuseConfirmationService,
  ) {
    super();
  }

  public magicLogin(data: { token: string }): Observable<IMagicLogin> {
    return this.http
      .post<{ data: IMagicLoginServer }>(`${environment.apiUrl}/v2/magic-login`, data)
      .pipe(map((response: { data: IMagicLoginServer }): IMagicLogin => this.transformLoginData(response.data)));
  }

  public changeWelcomeStatus(): Observable<any> {
    return this.http.get(`${environment.sociuuhubApiUrl}/user/change-welcome-status`);
  }

  public login(email: string, password: string): Observable<ILogin> {
    const data = {
      email: email,
      password: password,
    };

    return this.http.post<ILoginServer>(`${environment.apiUrl}/login`, data)
      .pipe(
        map((response: ILoginServer): ILogin => {
          return {
            success: response.success,
            data: this.transformLoginData(response.data),
            messages: response?.messages,
            changePassword: response.change_password,
            token: response.token,
          };
        })
      );
  }

  public enable2fa(secret: string, user_id: number, challengeToken?: string | null): Observable<IEnable2faResponse> {
    // user_id is only read by APIs that predate the login challenge; newer APIs verify against challenge_token.
    const data = { secret, user_id, challenge_token: challengeToken ?? undefined };
    return this.http.post<IEnable2faResponse>(`${environment.apiUrl}/enable2fa`, data);
  }

  public getBecomeUserUrl(userId: number): Observable<IBecomeUser> {
    return this.http.get<IBecomeUserServer>(`${environment.apiUrl}/v2/sociuuhub/become/${userId}`).pipe(
      map(
        (response: IBecomeUserServer): IBecomeUser => ({
          url: response.data.url,
        })
      )
    );
  }

  public getClientInfo(): Observable<ClientInfoResponseDto> {
    if (!this._clientInfoRequest$) {
      this._clientInfoRequest$ = this.http.get<ClientInfoResponseDto>(`${environment.apiUrl}/client/info`)
        .pipe(
          tap((response: ClientInfoResponseDto) => {
            this.isBrandwatchIntegrationEnabled
              .next(response.data.integrations?.some((item: string): boolean => item === INTEGRATIONS.BRANDWATCH));
            this.isActimoIntegrationEnabled
              .next(response.data.integrations?.some((item: string): boolean => item === INTEGRATIONS.ACTIMO));

            if (response.success) {
              const clientInfo = this.normalizeClientInfo(response.data);

              this.i18nService.setLocale(response?.data?.i18n || 'en');
              this.clientInfo.next(clientInfo);

              // Update Sentry context with company name
              setSentryContext({
                companyName: clientInfo?.client,
              });
            }
          }),
          catchError((error) => {
            this.notificationService.showError(getValidationErrorMessage(error));
            console.error('Error fetching client info:', getValidationErrorMessage(error));
            this._clientInfoRequest$ = null;
            return of({ success: false, data: {} as ClientInfoDto } as ClientInfoResponseDto);
          }),
          shareReplay(1)
        );
    }
    return this._clientInfoRequest$;
  }

  public refreshUserToken(): Observable<RefreshTokenDto> {
    return this.http.get<RefreshTokenDto>(`${environment.apiUrl}/auth/refresh`);
  }

  public logout(queryParams?: NavigationExtras): void {
    Sentry.setUser(null);
    clearSentryContext(); // Clear Sentry error context
    if (this.isBecomeUserEnabled) this.notificationService.showInfo('Your session has been expired.');

    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    this._currentUser$.next(null as unknown as UserInfoDto);
    this.echoService.resetEchoAuth();
    this._clientInfoRequest$ = null;
    this._clientInfo$.next(null as unknown as ClientInfoDto);
    this._isBrandwatchIntegrationEnabled.next(false);
    this._isActimoIntegrationEnabled.next(false);
    this.router.navigate(['/login'], queryParams);
  }

  public validateUserToken(token: string, type: string): Observable<DefaultResponseDto> {
    let params = new HttpParams();
    params = params.append('token', `${token}`);
    params = params.append('type', `${type}`);
    return this.http.get<DefaultResponseDto>(`${environment.apiUrl}/account/validate`, { params: params }).pipe(shareReplay());
  }

  private transformLoginData(loginData: IMagicLoginServer): IMagicLogin {
    return {
      token: loginData.token,
      is2fa: loginData.is_2fa,
      secret: loginData?.secret,
      isAdmin2fa: loginData.is_admin_2fa,
      google2faUrl: loginData?.google2fa_url,
      userId: loginData?.user_id,
      challengeToken: loginData?.challenge_token ?? null,
    };
  }

  public activateUser(data: { email: string }): Observable<IAccountReset> {
    return this.http.post<{ data: IAccountReset }>(`${environment.baseUrl}/auth/account-reset/activation`, data)
      .pipe(map((response: { data: IAccountReset }): IAccountReset => response.data));
  }

  public resetPasswordUser(data: { email: string }): Observable<IAccountReset> {
    return this.http.post<{ data: IAccountReset }>(`${environment.baseUrl}/auth/account-reset/reset`, data)
      .pipe(map((response: { data: IAccountReset }): IAccountReset => response.data));
  }

  public updateAccount(data: IAccountUpdatePayload): Observable<IAccountUpdate> {
    return this.http.post<{ data: IAccountUpdate }>(`${environment.baseUrl}/auth/account-update`, data)
      .pipe(map((response: { data: IAccountUpdate }): IAccountUpdate => response.data));
  }

  public openRestrictedActionDialog(message: string = RESTRICTED_ACTION_MESSAGE): void {
    const restrictedActionModal: FuseConfirmationConfig = {
      title: `Restricted Action`,
      message: message,
      icon: {
        show: true,
        color: 'warn',
        name: 'heroicons_outline:exclamation',
      },
      actions: {
        cancel: {
          show: true,
          label: 'Close',
        },
        confirm: {
          show: false,
        }
      },
      dismissible: true,
    };
    let dialogRef: MatDialogRef<FuseConfirmationDialogComponent, any>;
    dialogRef = this._fuseConfirmationService.open(restrictedActionModal);
  }

  public resetCookieConsentAccepted(): void {
    this.cookieService.delete('cookieConsentAccepted');
  }

  public handleCookieConsentAccepted(): void {
    this.cookieService.set('cookieConsentAccepted', 'true', { expires: 365 });
  }

  private normalizeClientInfo(data: ClientInfoDto): ClientInfoDto {
    if (!data) return data;

    const companyLogo = `${data.company_logo || ''}`.trim();
    const isBrowserResolvableLogo = /^(https?:\/\/|\/\/|data:image\/|assets\/|\/assets\/)/i.test(companyLogo);

    return {
      ...data,
      company_logo: isBrowserResolvableLogo ? companyLogo : DEFAULT_COMPANY_LOGO,
    };
  }
}
